import type { AssessmentFilterOptionsResponse } from "../services/assessmentCenterService";

/** Figma — Status column pill when assessment is in progress. */
export const ASSESSMENT_IN_PROGRESS_STATUS_PILL = {
  label: "In Progress",
  backgroundColor: "#E7ECFC",
  color: "#295EF1",
} as const;

/** Same mapping as Regulation Details → Assessment History table. */
export function formatAssessmentHistoryStatus(status: string): string {
  const statusMap: Record<string, string> = {
    init: "In Progress",
    completed: "Completed",
    success: "Completed",
    failed: "Failed",
    partial: "Partially Failed",
    pending: "Pending",
    running: "In Progress",
    in_progress: "In Progress",
    inprogress: "In Progress",
  };
  const key = status.trim().toLowerCase();
  return statusMap[key] || status.trim() || "—";
}

export function normalizeAssessmentStatusKey(status: string): string {
  return status.trim().toLowerCase().replace(/\s+/g, "_");
}

export function isAssessmentInProgressStatus(normalized: string): boolean {
  return (
    normalized === "in_progress" ||
    normalized === "inprogress" ||
    normalized === "running" ||
    normalized === "init" ||
    normalized === "pending"
  );
}

/** Chip colors for the formatted status label (Assessment History parity). */
export function getAssessmentHistoryStatusChipStyle(value: string): {
  backgroundColor: string;
  color: string;
} {
  const normalized = normalizeAssessmentStatusKey(value);
  const normalizedSpaced = value.trim().toLowerCase();
  if (
    normalized === "completed" ||
    normalized === "complete" ||
    normalized === "success"
  ) {
    return { backgroundColor: "#dcfce7", color: "#166534" };
  }
  if (normalized === "failed" || normalized === "partially_failed") {
    return { backgroundColor: "#fee2e2", color: "#991b1b" };
  }
  if (
    isAssessmentInProgressStatus(normalized) ||
    normalizedSpaced === "in progress"
  ) {
    return {
      backgroundColor: ASSESSMENT_IN_PROGRESS_STATUS_PILL.backgroundColor,
      color: ASSESSMENT_IN_PROGRESS_STATUS_PILL.color,
    };
  }
  return { backgroundColor: "#fef3c7", color: "#92400e" };
}

/** Figma pill label + colors for AI / Assessment History status cells. */
export function getAiAssessmentStatusPillPresentation(rawStatus: unknown): {
  label: string;
  backgroundColor: string;
  color: string;
  showInfoIcon: boolean;
} | null {
  const rawStr = typeof rawStatus === "string" ? rawStatus : "";
  if (!rawStr.trim()) return null;

  const normalized = normalizeAssessmentStatusKey(rawStr);
  const display = formatAssessmentHistoryStatus(rawStr);
  if (!display || display === "—" || display === "-") return null;

  if (normalized === "partial") {
    return {
      label: "Partially",
      backgroundColor: "#FFF0B3",
      color: "#C64A10",
      showInfoIcon: true,
    };
  }

  if (normalized === "failed" || normalized === "failure") {
    return {
      label: capitalizeHistoryStatusChipLabel(display),
      backgroundColor: "#fee2e2",
      color: "#991b1b",
      showInfoIcon: true,
    };
  }

  if (
    isAssessmentInProgressStatus(normalized) ||
    display === ASSESSMENT_IN_PROGRESS_STATUS_PILL.label
  ) {
    return {
      label: ASSESSMENT_IN_PROGRESS_STATUS_PILL.label,
      backgroundColor: ASSESSMENT_IN_PROGRESS_STATUS_PILL.backgroundColor,
      color: ASSESSMENT_IN_PROGRESS_STATUS_PILL.color,
      showInfoIcon: false,
    };
  }

  const chipStyle = getAssessmentHistoryStatusChipStyle(display);
  const label =
    normalized === "completed" || normalized === "complete" || normalized === "success"
      ? "Completed"
      : capitalizeHistoryStatusChipLabel(display);

  return {
    label,
    ...chipStyle,
    showInfoIcon: false,
  };
}

/** Assessment History status column: title-case-ish label on the chip. */
export function capitalizeHistoryStatusChipLabel(value: string): string {
  if (!value || value === "—" || value === "-") return value;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** UI label for filter-options entries (e.g. keyword → Keyword). */
export function formatFilterOptionDisplayName(name: string): string {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/** Query param value from filter-options — prefer API `value`, keep lowercase. */
export function pickFilterOptionQueryValue(opt: {
  name: string;
  value?: string | string[] | null;
}): string {
  if (typeof opt.value === "string" && opt.value.trim() !== "") {
    return opt.value.trim();
  }
  return String(opt.name ?? "").trim().toLowerCase();
}

/**
 * Maps a selected dropdown **display name** to API query value(s), matching
 * Assessment History `appendFilterParam` (filter-options `name` → `value`).
 */
export function resolveAssessmentFilterOptionQueryValues(
  filterConfigs: AssessmentFilterOptionsResponse | undefined,
  filterName: string,
  selectedOptionName: string,
): string[] | undefined {
  if (!selectedOptionName?.trim()) return undefined;
  if (!filterConfigs?.length) return undefined;

  const filter = filterConfigs.find((f) => f.name === filterName);
  const opt = filter?.options.find((o) => o.name === selectedOptionName);

  if (opt) {
    if (Array.isArray(opt.value)) {
      return opt.value.map(String);
    }
    return [String(opt.value)];
  }

  return [selectedOptionName];
}

/** Status column: prefer API `rawStatus`, fall back to formatted `status` field. */
export function resolveAssessmentStatusForPill(
  rawStatus: unknown,
  formattedStatus?: unknown,
): string {
  if (typeof rawStatus === "string" && rawStatus.trim()) {
    return rawStatus.trim();
  }
  if (typeof formattedStatus === "string" && formattedStatus.trim()) {
    return formattedStatus.trim();
  }
  return "";
}
